import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useSleepTracking } from '@/hooks/useSleepTracking';
import { getHealthConnectedCacheSync, getHealthConnectedState } from '@/lib/appleHealth';
import { getLocalDayKey, getLocalDateDaysAgo } from '@/lib/localDateRange';
import {
  computeCompositeRecoveryPercent,
  type HealthRecoveryMetricRow,
  type WorkoutForRecovery,
} from '@/lib/recoveryScore';

/**
 * Single source of truth for the home / dashboard **recovery %** so sleep,
 * Apple Health vitals (when connected + present), and recent workouts stay
 * aligned. See `src/lib/recoveryScore.ts` module doc for the full formula.
 */
export function useRecoveryScore() {
  const { user } = useAuth();
  const { mergedTodaySleep } = useSleepTracking();

  const [appleHealthConnected, setAppleHealthConnected] = useState(() =>
    getHealthConnectedCacheSync(user?.id)
  );
  const [metricRows, setMetricRows] = useState<HealthRecoveryMetricRow[]>([]);
  const [recentWorkouts, setRecentWorkouts] = useState<WorkoutForRecovery[]>([]);
  const workoutRefreshTimerRef = useRef<number | null>(null);

  useEffect(() => {
    setAppleHealthConnected(getHealthConnectedCacheSync(user?.id));
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const ok = await getHealthConnectedState(user?.id);
      if (!cancelled) setAppleHealthConnected(ok);
    })();
    const onSync = () => {
      void getHealthConnectedState(user?.id).then(setAppleHealthConnected);
    };
    window.addEventListener('health-sync-complete', onSync);
    return () => {
      cancelled = true;
      window.removeEventListener('health-sync-complete', onSync);
    };
  }, [user?.id]);

  const fetchMetrics = useCallback(async () => {
    if (!user?.id || !appleHealthConnected) {
      setMetricRows([]);
      return;
    }
    const start = getLocalDateDaysAgo(15);
    const startStr = format(start, 'yyyy-MM-dd');
    const { data, error } = await supabase
      .from('health_metrics')
      .select('metric_date, hrv_sdnn_ms, resting_heart_rate_bpm')
      .eq('user_id', user.id)
      .gte('metric_date', startStr)
      .order('metric_date', { ascending: false });
    if (error) {
      console.warn('[useRecoveryScore] health_metrics fetch failed:', error.message);
      setMetricRows([]);
      return;
    }
    setMetricRows((data ?? []) as HealthRecoveryMetricRow[]);
  }, [user?.id, appleHealthConnected]);

  useEffect(() => {
    void fetchMetrics();
  }, [fetchMetrics]);

  const fetchRecentWorkouts = useCallback(async () => {
    if (!user?.id) {
      setRecentWorkouts([]);
      return;
    }

    const since = new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString();
    const { data, error } = await supabase
      .from('workouts')
      .select('completed_at, duration_minutes')
      .eq('user_id', user.id)
      .not('completed_at', 'is', null)
      .gte('completed_at', since)
      .order('completed_at', { ascending: false });

    if (error) {
      console.warn('[useRecoveryScore] recent workouts fetch failed:', error.message);
      setRecentWorkouts([]);
      return;
    }

    setRecentWorkouts((data ?? []) as WorkoutForRecovery[]);
  }, [user?.id]);

  useEffect(() => {
    void fetchRecentWorkouts();
  }, [fetchRecentWorkouts]);

  useEffect(() => {
    if (!user?.id) return;

    const scheduleRefresh = () => {
      if (workoutRefreshTimerRef.current != null) return;
      workoutRefreshTimerRef.current = window.setTimeout(() => {
        workoutRefreshTimerRef.current = null;
        void fetchRecentWorkouts();
      }, 500);
    };

    const channelInstanceId = crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const channel = supabase
      .channel(`recovery-workouts-${user.id}-${channelInstanceId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'workouts',
          filter: `user_id=eq.${user.id}`,
        },
        scheduleRefresh
      )
      .subscribe();

    return () => {
      if (workoutRefreshTimerRef.current != null) {
        window.clearTimeout(workoutRefreshTimerRef.current);
        workoutRefreshTimerRef.current = null;
      }
      supabase.removeChannel(channel);
    };
  }, [fetchRecentWorkouts, user?.id]);

  useEffect(() => {
    const onSync = () => {
      void fetchMetrics();
    };
    window.addEventListener('health-sync-complete', onSync);
    return () => window.removeEventListener('health-sync-complete', onSync);
  }, [fetchMetrics]);

  const recoveryPercent = useMemo(() => {
    const sleepHours = mergedTodaySleep.hours ?? 0;
    const sleepQuality =
      mergedTodaySleep.quality != null
        ? Math.min(5, Math.max(1, mergedTodaySleep.quality))
        : sleepHours > 0
          ? 4
          : 0;

    return computeCompositeRecoveryPercent({
      sleepHours,
      sleepQuality1to5: sleepQuality,
      appleHealthConnected,
      todayLocalKey: getLocalDayKey(),
      metricRows,
        workouts: recentWorkouts,
    });
  }, [mergedTodaySleep, appleHealthConnected, metricRows, recentWorkouts]);

  return { recoveryPercent, appleHealthConnected, recentWorkouts };
}
